<template>
  <div class="container thankyou-page">
    <block-breadcrumbs :items="[{ name: 'Спасибо за заказ' }]" />
    <!-- banner -->
    <page-info
      :title="title"
      :subtitle="subtitle"
      :icon="icon"
    />
    <order-card
      class="thankyou-page__card"
      v-for="(order, index) in orderNumbers"
      ref="orderCard"
      :key="order"
      :order-number="order"
      :is-last="isLast(index)"
      :show-support-info="isLast(index)"
      :is-expanded-default="orderNumbers.length === 1"
      location="typ"
      @order-data-loaded="activatePrepayOrder(index, $event)"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { createOrdersSessionStorage } from '@/services/storage/orders-storage';

/* Icons */
import IconSucceed from '@images/success.multi.svg';
import IconFailed from '@images/failed.multi.svg';

/* Components */
import OrderCard from '@/components/account/order-card/order-card.vue';
import blockBreadcrumbs from '@/components/breadcrumbs/breadcrumbs.vue';
import PageInfo from '@/components/common/page-info.vue';
import pageMetaMixin from '@/mixins/page-meta-mixin';
import { typGtmEvents } from '@/gtm/typ-gtm-events';

export default {
  name: 'thankyou-page',

  metaInfo() {
    return this.getPageMeta({ pageTitle: 'Спасибо за заказ' });
  },

  components: {
    blockBreadcrumbs,
    PageInfo,
    OrderCard,
  },

  mixins: [pageMetaMixin],

  icons: {
    IconSucceed,
    IconFailed,
  },

  data: () => ({
    orderNumbers: [],
    notPaidOrders: [],
    needPrepayOrder: false,
    mostExpensiveOrderNumber: '',
  }),

  computed: {
    queryOrderNumbers() {
      return this.$route.query?.orders?.split(',') || [];
    },

    paymentStatus() {
      return this.$route.query?.status || '';
    },

    icon() {
      switch(this.paymentStatus) {
        case 'failed':
          return this.$options.icons.IconFailed.id
        default:
          return this.$options.icons.IconSucceed.id
      }
    },

    title() {
      switch(this.paymentStatus) {
        case 'succeed':
          return 'Ваш заказ оплачен';
        case 'failed':
          return 'Ошибка оплаты';
        default:
          return 'Спасибо за заказ!';
      }
    },

    redirectedPaidOrderNumber() {
      if (this.paymentStatus === 'succeed' && this.queryOrderNumbers.length === 1) {
        return this.queryOrderNumbers[0];
      }

      return null;
    },

    payStatuses() {
        return {
          paid: [this.redirectedPaidOrderNumber],
          notPaid: this.notPaidOrders.filter(i => i !== this.redirectedPaidOrderNumber),
        }
    },

    subtitle() {
      switch(this.paymentStatus) {
        case 'succeed':
          return this.getSucceedSubtitle();
        case 'failed':
          return 'Платёж не прошёл. Пожалуйста, попробуйте произвести оплату заказа ещё раз';
        default:
          if (this.needPrepayOrder) {
            return 'Ваш заказ оформлен и ожидает оплаты';
          } else {
            return '';
          }
      }
    },

    ordersStorage: () => createOrdersSessionStorage(),
  },

  async serverPrefetch () {
    const orderNumber = this.$route.query.succeed || this.$route.query.failed;

    if (orderNumber) {
      if (this.$route.query.succeed) {
        try {
          await this.$store.$api.acquiringSuccess({
            orderNumber,
            aqcuiringParameters: `orderId=${this.$route.query.orderId}&lang=${this.$route.query.lang}`,
          });
        } catch (e) {
          return Promise.reject({ status: 302, redirect: `/account/orders` });
        }
      }
      return Promise.reject({ status: 302, redirect: `/thankyoupage?orders=${orderNumber}&status=${this.$route.query.succeed ? 'succeed' : 'failed'}` });
    }
    else if (!this.queryOrderNumbers.length) {
      // на всякий случай если в урле нет заказов, редиректим в лк
      return Promise.reject({ status: 302, redirect: '/account/orders' });
    }
  },

  mounted() {
    this.loadOrdersData();
    const orderNumber = this.redirectedPaidOrderNumber;
    if (orderNumber) typGtmEvents.paidOnline({ orderNumber });
  },

  methods: {
    ...mapActions('cart', ['getFromStorageOrderNumbers']),

    isLast(index) {
      return index === this.orderNumbers.length -1;
    },

    async getOrderNumbers() {
      try {
        const fromLocalstorage = await this.getFromStorageOrderNumbers();

        // убираем дубли
        return [...new Set([...this.queryOrderNumbers, ...fromLocalstorage])];

      } catch (error) {
        console.log(error)
      }
    },

    async loadOrdersData() {
      try {
        this.orderNumbers = await this.getOrderNumbers();
        const orderDetails = await this.$store.$api.getOrderDetails({orderNumbers: this.orderNumbers});

        const sortedOrders = orderDetails
          .slice()
          .sort((orderA, orderB) => parseFloat(orderB.subtotal, 10) - parseFloat(orderA.subtotal, 10));
        this.mostExpensiveOrderNumber = sortedOrders[0]?.orderNumber;

        for await (const orderDetail of orderDetails) {
          const { totalPaid, tools } = await this.$store.$api.getPaymentTools({
            orderNumber: orderDetail.orderNumber
          });
          const isPaid = !tools?.length && totalPaid >= orderDetail.subtotal;

          if (!isPaid) {
            this.notPaidOrders.push(orderDetail.orderNumber);
          }
        }
      } catch (error) {
        console.log(error)
      }
    },

    getSucceedSubtitle() {
      const format = (i) => `№${i}`;
      const getPaidTitle = () => {
        return this.payStatuses.notPaid.length > 0
          ? `Благодарим вас за оплату заказа ${format(this.redirectedPaidOrderNumber)}.`
          : 'Благодарим вас за оплату заказа';
      };
      const getNotPaidTitle = () => {
        const notPaidLength = this.payStatuses.notPaid.length;
        const ordersWord = this.$options.filters.decl(notPaidLength, 'Заказ', ['', 'ы', 'ов']);
        const formatedNotPaidNumbers = this.payStatuses.notPaid.map(format).join(', ');
        const awaitsWord = this.$options.filters.decl(notPaidLength, 'ожида', ['ет', 'ют', 'ет']);

        return `${ordersWord} ${formatedNotPaidNumbers} ${awaitsWord} оплаты`;
      };

      const paidTitlePart = getPaidTitle();
      const notPaidTitlePart = getNotPaidTitle();

      if (this.payStatuses.notPaid.length > 0) {
        return `${paidTitlePart} ${notPaidTitlePart}`
      }

      return paidTitlePart;
    },

    activatePrepayOrder(index, order) {
      const {orderNumber, needPrepay } = order;
      this.needPrepayOrder = !!needPrepay;

      if (orderNumber !== this.mostExpensiveOrderNumber) return;

      const fpsOrder = this.ordersStorage.ordersWithFps[orderNumber];

      // открываем модалку СБП один раз на странице TYP
      if (fpsOrder && !fpsOrder.isWatched) {
        this.$refs.orderCard[index]?.openFpsPaymentModal();
        this.ordersStorage.updateOrderWithFps(orderNumber, {
          ...fpsOrder,
          isWatched: true,
        });
      }
    }
  },
};
</script>

<style lang="scss">
.thankyou-page {
  .page-info {
    margin: 48px 0;
  }
  .loader {
    height: 100%;
    color: $blue;
  }
  &__card {
    margin-bottom: 22px;
    &:last-child {
      margin-bottom: 0;
    }
  }
}

@include breakpoint("tablet") {
  .thankyou-page {
    .page-info {
      margin: 56px 0;
    }
    &__card {
      margin-bottom: 32px;
    }
  }
}

@media print {
  .thankyou-page {
    .page-info {
      display: none;
    }
  }
}
</style>
